import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBillingContext } from "../../contexts/BillingContext";
import { isPathAllowedWithoutTier1 } from "../../utils/billingAllowlist";
import { isBillingStatusUnreachable } from "../../utils/billingStatusAvailability";
import {
  redirectFromBillingPaywall,
  redirectToBillingPaywall,
} from "../../utils/billingPaywall";
import { getPendingDemoClaimResumePath } from "../../utils/demoEntryAnalysisSession";
import {
  redirectFromServiceUnavailable,
  redirectToServiceUnavailable,
  SERVICE_UNAVAILABLE_PATH,
} from "../../utils/serviceUnavailableRedirect";

const PAYWALL_PATH = "/paywall";

function navigateToDemoClaimResume(path: string): void {
  try {
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  } catch {
    window.location.assign(path);
  }
}

/**
 * After billing status loads, redirect unsubscribed users to `/paywall`
 * unless they are on an allowlisted route (profile, billing return URLs).
 * When billing status cannot be loaded due to downtime, send users to `/service-unavailable`.
 */
export default function BillingPaywallEnforcer({ children }: { children: React.ReactNode }) {
  const { isLoading, isTier1Granted, loadError } = useBillingContext();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const billingUnreachable = isBillingStatusUnreachable(loadError);

    if (billingUnreachable) {
      if (pathname !== SERVICE_UNAVAILABLE_PATH) {
        redirectToServiceUnavailable();
      }
      return;
    }

    if (pathname === SERVICE_UNAVAILABLE_PATH) {
      if (!loadError) {
        redirectFromServiceUnavailable(isTier1Granted ? "/dashboard" : PAYWALL_PATH);
      }
      return;
    }

    // Demo Entry Analysis: after signup Clerk may land on /dashboard or /paywall.
    // Resume claim before paywalling so users see their report results.
    const demoResume = getPendingDemoClaimResumePath();
    if (demoResume) {
      const alreadyOnResume =
        pathname.startsWith("/demo/entry-analysis/generating") ||
        pathname.startsWith("/demo/entry-analysis/results");
      if (!alreadyOnResume) {
        navigateToDemoClaimResume(demoResume);
        return;
      }
    }

    if (isTier1Granted) {
      if (pathname === PAYWALL_PATH) {
        redirectFromBillingPaywall();
      }
      return;
    }

    if (pathname === PAYWALL_PATH) {
      return;
    }
    if (isPathAllowedWithoutTier1(pathname)) {
      return;
    }

    redirectToBillingPaywall();
  }, [isLoading, isTier1Granted, loadError, location.pathname]);

  return <>{children}</>;
}
